import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { SiteLogo } from "@/components/SiteLogo";
import { digitsOnly, gameIdError, waError, loadPlayer, savePlayer, clearPlayer, newRequestId, GAME_ID_LABEL } from "@/lib/member";
import { ProofUpload } from "@/components/member/ProofUpload";
import { SavedPlayerBar } from "@/components/member/FormHelpers";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useNickCheck, NickStatus, nickBlockMsg } from "@/components/member/NickCheck";

const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };

const Field = ({ label, hint, error, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-extrabold text-slate-800">{label}</label>
    {children}
    {error ? <p className="mt-1 text-[11px] font-bold text-rose-500">{error}</p> : hint ? <p className="mt-1 text-[11px] font-bold text-slate-500">{hint}</p> : null}
  </div>
);

const MemberTerms = ({ cfg, game }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm font-bold text-slate-800" data-testid="member-terms">
    <div className="font-extrabold text-amber-700">Syarat Harga Member</div>
    <div className="mt-1">Harga member {GAMES[game]}: <span data-testid="terms-price">{formatRupiah(cfg.prices[game])}</span> / 1B</div>
    <div className="mt-2 font-extrabold text-amber-700">Biaya Admin</div>
    <div data-testid="terms-fee">Total biaya admin: {formatRupiah(cfg.admin_fee_min)} – {formatRupiah(cfg.admin_fee_max)}</div>
  </div>
);

const DestIdBox = ({ game, id }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success(`ID tujuan ${id} berhasil disalin`);
    } catch {
      toast.error("Gagal menyalin, salin manual");
    }
  };
  return (
    <button type="button" onClick={copy} className="kz-dest-box mx-auto block w-full rounded-2xl p-5 text-center text-white" data-testid="dest-id-box">
      <div className="text-xs font-extrabold uppercase tracking-widest opacity-90">Kirim chip ke ID {GAMES[game]}</div>
      <div className="mt-1 flex items-center justify-center gap-3 font-mono text-4xl font-black tracking-wider" data-testid="dest-id-value">
        {id} <Copy size={22} />
      </div>
      <div className="mt-1 text-[11px] font-bold opacity-90">Ketuk untuk menyalin</div>
    </button>
  );
};

const Success = () => (
  <div className="py-6 text-center" data-testid="bongkar-success">
    <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
    <h2 className="mt-3 text-2xl font-black text-slate-800">Bongkaran Sedang Diproses!</h2>
    <p className="mt-2 text-sm font-bold text-slate-600">Harap bersabar dan mengantri, Pasti Kami bayar 100% 🥰</p>
    <Link to="/" className="kz-outline-btn mx-auto mt-5 max-w-xs" data-testid="bongkar-back-home-success">Kembali ke Beranda</Link>
  </div>
);

export const BongkarPage = () => {
  const saved = loadPlayer();
  const { games_enabled: ge } = useSiteSettings();
  const activeGames = Object.entries(GAMES).filter(([k]) => ge?.[k] !== false);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ product_id: "", game_id: "", chip: "", bank: "", rek_number: "", rek_name: "", wa_number: saved?.wa || "" });
  const [proofId, setProofId] = useState(null);
  const [requestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [hasSaved, setHasSaved] = useState(Boolean(saved));

  useEffect(() => {
    api.get("/bongkar/config").then((r) => setCfg(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nick = useNickCheck(form.product_id, form.game_id);
  const chipNum = Number(form.chip || 0);
  const maxB = cfg?.max_b || 40;
  const errors = {
    game_id: form.game_id && gameIdError(form.game_id) ? `Player ID ${gameIdError(form.game_id)}` : "",
    chip: form.chip && (chipNum < 1 || chipNum > maxB) ? `Nominal harus 1B – ${maxB}B` : "",
    wa_number: form.wa_number && waError(form.wa_number) ? waError(form.wa_number) : "",
    rek_number: form.rek_number && form.rek_number.length < 4 ? "Nomor rekening minimal 4 digit" : "",
  };
  const valid = form.product_id && form.game_id && !gameIdError(form.game_id) && chipNum >= 1 && chipNum <= maxB && form.bank
    && form.rek_number.length >= 4 && form.rek_name.trim().length >= 2 && !waError(form.wa_number) && proofId && !nickBlockMsg(nick);

  const pickGame = (pid) => setForm((f) => ({ ...f, product_id: pid, game_id: f.game_id || saved?.ids?.[pid] || "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return toast.error(nickBlockMsg(nick) || "Lengkapi semua data dan upload bukti transfer chip");
    setBusy(true);
    try {
      await api.post("/buyback", {
        product_id: form.product_id, game_id: form.game_id, wa_number: form.wa_number, chip_b: chipNum, bank: form.bank,
        rek_number: form.rek_number, rek_name: form.rek_name, proof_id: proofId, client_request_id: requestId,
      });
      savePlayer(form.product_id, form.game_id, form.wa_number);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengirim request"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="kz-light-page relative z-10 px-4 pb-32 pt-6 font-bold" data-testid="bongkar-page">
      <div className="mx-auto max-w-xl text-center">
        <SiteLogo className="mx-auto h-28 w-28 rounded-2xl bg-black object-contain p-1 shadow-lg" testid="bongkar-logo" />
        <h1 className="mt-3 text-4xl font-black text-[#2563eb] sm:text-5xl">JP STORE</h1>
        <p className="mt-1 text-xs text-slate-500">Terpercaya • Premium • 24/7</p>
      </div>
      <div className="mx-auto mt-6 max-w-xl rounded-2xl bg-white p-6 text-left shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] sm:p-8">
        {done ? <Success /> : !cfg ? <p className="text-center text-sm text-slate-500">Memuat…</p> : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center">
              <h2 className="text-2xl font-black text-slate-800">Form Bongkar</h2>
              <p className="mt-1 text-xs text-slate-500">Lengkapi data berikut untuk mengirim request bongkar</p>
            </div>
            {hasSaved ? (
              <SavedPlayerBar light saved={saved?.wa || "-"} onUse={() => set("wa_number", saved?.wa || "")} onClear={() => { clearPlayer(); setHasSaved(false); }} />
            ) : null}
            <Field label="Pilih Game">
              <select value={form.product_id} onChange={(e) => pickGame(e.target.value)} className="kz-light-input font-bold" data-testid="bongkar-game-select">
                <option value="">Pilih game</option>
                {activeGames.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            {form.product_id ? (
              <>
                <MemberTerms cfg={cfg} game={form.product_id} />
                <DestIdBox game={form.product_id} id={cfg.dest_ids[form.product_id]} />
              </>
            ) : null}
            <Field label={form.product_id ? `Player ID (${GAME_ID_LABEL[form.product_id]})` : "Player ID"} error={errors.game_id}>
              <input value={form.game_id} onChange={(e) => set("game_id", digitsOnly(e.target.value, 10))} inputMode="numeric" placeholder="Hanya angka, maks 10 digit" className="kz-light-input font-bold" data-testid="bongkar-game-id" />
              {!errors.game_id ? <NickStatus n={nick} light /> : null}
            </Field>
            <Field label="Nominal Bongkar" hint={`Minimal 1B, maksimal ${maxB}B dalam 1x request${chipNum ? ` · Estimasi ${formatRupiah(chipNum * (cfg.prices[form.product_id] || 0))}` : ""}`} error={errors.chip}>
              <div className="relative">
                <input value={form.chip} onChange={(e) => set("chip", digitsOnly(e.target.value, 3))} inputMode="numeric" placeholder="Contoh: 10" className="kz-light-input pr-12 font-bold" data-testid="bongkar-chip-amount" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-black text-blue-600" data-testid="bongkar-chip-display">{form.chip ? `${chipNum}B` : "B"}</span>
              </div>
            </Field>
            <Field label="Bank / E-Wallet">
              <select value={form.bank} onChange={(e) => set("bank", e.target.value)} className="kz-light-input font-bold" data-testid="bongkar-bank-select">
                <option value="">Pilih Bank</option>
                {cfg.banks.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Nomor Rekening" error={errors.rek_number}>
              <input value={form.rek_number} onChange={(e) => set("rek_number", digitsOnly(e.target.value, 30))} inputMode="numeric" placeholder="Masukkan nomor rekening" className="kz-light-input font-bold" data-testid="bongkar-rek-number" />
            </Field>
            <Field label="Nama Rekening">
              <input value={form.rek_name} onChange={(e) => set("rek_name", e.target.value)} placeholder="Nama sesuai rekening" className="kz-light-input font-bold" data-testid="bongkar-rek-name" />
            </Field>
            <Field label="Nomor WhatsApp" hint="Diawali 08, maks 15 digit" error={errors.wa_number}>
              <input value={form.wa_number} onChange={(e) => set("wa_number", digitsOnly(e.target.value, 15))} inputMode="tel" placeholder="08xxxxxxxxxx" className="kz-light-input font-bold" data-testid="bongkar-wa" />
            </Field>
            <Field label="Bukti Transfer Chip">
              <ProofUpload kind="bongkar" onChange={setProofId} dark={false} testid="bongkar-proof" />
            </Field>
            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={!valid || busy} className="kz-blue-btn !w-auto px-6 disabled:cursor-not-allowed disabled:opacity-50" data-testid="bongkar-submit">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Kirim Request Bongkar"}
              </button>
              <Link to="/" className="kz-outline-btn !w-auto px-6" data-testid="bongkar-back-home">Kembali ke Beranda</Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
};
